export const SCROLL_DURATION = 1.1;
export const WHEEL_MULTIPLIER = 1.3;
export const scrollEasing = (t: number) => 1 - (1 - t) * (1 - t);

type Axis = "x" | "y";
type Target = HTMLElement | "native" | "contain" | null;

interface Tween {
  from: number;
  to: number;
  start: number;
  written: number;
  frame: number;
}

const LINE_HEIGHT = 100 / 6;
const SCROLLABLE = /auto|scroll|overlay/;
const tweens = new WeakMap<HTMLElement, Partial<Record<Axis, Tween>>>();

const maxScroll = (el: HTMLElement, axis: Axis) =>
  axis === "y" ? el.scrollHeight - el.clientHeight : el.scrollWidth - el.clientWidth;

const position = (el: HTMLElement, axis: Axis) =>
  tweens.get(el)?.[axis]?.to ?? (axis === "y" ? el.scrollTop : el.scrollLeft);

// Innermost element under the cursor that can still scroll in the wheel's direction.
// The page itself (html/body) is left to Lenis.
function findTarget(event: WheelEvent, axis: Axis, delta: number): Target {
  for (const node of event.composedPath()) {
    if (node === document.body || node === document.documentElement) break;
    if (!(node instanceof HTMLElement)) continue;

    const style = getComputedStyle(node);
    const overflow = axis === "y" ? style.overflowY : style.overflowX;
    const max = SCROLLABLE.test(overflow) ? maxScroll(node, axis) : 0;

    if (max > 1) {
      // Animating scrollTop against mandatory/proximity snapping makes the browser re-snap every frame.
      if (style.scrollSnapType !== "none") return "native";

      const pos = position(node, axis);
      if (delta > 0 ? pos < max - 0.5 : pos > 0.5) return node;

      // At its end: swallow the wheel rather than chaining it to the page, so scrolling a list never moves the layout
      return "contain";
    }

    if (node.hasAttribute("data-lenis-prevent")) return "native";
  }
  return null;
}

function animate(el: HTMLElement, axis: Axis, delta: number) {
  const state = tweens.get(el) ?? {};
  tweens.set(el, state);

  const prev = state[axis];
  if (prev) cancelAnimationFrame(prev.frame);

  const current = axis === "y" ? el.scrollTop : el.scrollLeft;
  const to = Math.max(0, Math.min(maxScroll(el, axis), (prev?.to ?? current) + delta));
  const tween: Tween = { from: current, to, start: performance.now(), written: current, frame: 0 };
  state[axis] = tween;

  const step = (now: number) => {
    const live = axis === "y" ? el.scrollTop : el.scrollLeft;
    // Something else moved it (scrollbar drag, keyboard, scrollIntoView): stop fighting it.
    if (Math.abs(live - tween.written) > 2) {
      delete state[axis];
      return;
    }

    const progress = Math.min(1, (now - tween.start) / (SCROLL_DURATION * 1000));
    const value = tween.from + (tween.to - tween.from) * scrollEasing(progress);
    el.scrollTo({ [axis === "y" ? "top" : "left"]: value, behavior: "instant" });
    tween.written = axis === "y" ? el.scrollTop : el.scrollLeft;

    if (progress < 1) tween.frame = requestAnimationFrame(step);
    else delete state[axis];
  };
  tween.frame = requestAnimationFrame(step);
}

// Safari and every iOS browser (all WebKit). WebKit caps requestAnimationFrame at 60fps
// while its native scroll runs off the main thread at the full refresh rate, so JS smoothing
// of a trackpad (which already has native momentum) only makes it lag. Chromium UAs also
// contain "AppleWebKit", hence the exclusion.
const isWebKit = () => {
  const ua = navigator.userAgent;
  return /AppleWebKit/.test(ua) && !/Chrome|Chromium|Android/.test(ua);
};

const GESTURE_GAP = 250;
let nativeTrackpad = false;
let gesture = { native: false, last: -Infinity };

// WebKit reports the legacy wheelDelta as -3 × delta rounded to an integer. Trackpads and
// Magic Mouse send whole-pixel deltas, so the two match exactly; a mouse wheel notch is a
// fractional line (e.g. 4.000244px), so they never do. Decided per gesture so one gesture
// isn't split between native and smoothed scrolling. Once any event matches it stays native.
function isTrackpadGesture(event: WheelEvent) {
  const legacy = event as WheelEvent & { wheelDeltaX?: number; wheelDeltaY?: number };
  const matches = legacy.wheelDeltaY === -3 * event.deltaY && legacy.wheelDeltaX === -3 * event.deltaX;

  if (event.timeStamp - gesture.last > GESTURE_GAP) gesture = { native: matches, last: event.timeStamp };
  else gesture = { native: gesture.native || matches, last: event.timeStamp };
  return gesture.native;
}

// Runs in the capture phase, ahead of Lenis's window listener, and flags claimed events so Lenis skips them.
function onWheel(event: WheelEvent) {
  if (event.ctrlKey) return;

  // Safari trackpad: leave page and nested scrolling native. A mouse wheel still gets the Lenis glide,
  // since Safari stops a native wheel scroll dead the moment the wheel stops.
  if (nativeTrackpad && isTrackpadGesture(event)) {
    (event as WheelEvent & { lenisStopPropagation?: boolean }).lenisStopPropagation = true;
    stopPageGlide();
    return;
  }

  const axis: Axis = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? "x" : "y";
  const multiplier =
    event.deltaMode === 1 ? LINE_HEIGHT : event.deltaMode === 2 ? (axis === "y" ? innerHeight : innerWidth) : 1;
  const delta = (axis === "y" ? event.deltaY : event.deltaX) * multiplier * WHEEL_MULTIPLIER;
  if (!delta) return;

  const target = findTarget(event, axis, delta);
  if (!target) return;

  (event as WheelEvent & { lenisStopPropagation?: boolean }).lenisStopPropagation = true;
  stopPageGlide();
  if (target === "native") return;

  event.preventDefault();
  if (target !== "contain") animate(target, axis, delta);
}

let stopPageGlide = () => {};

// `onNestedScroll` halts the page's own glide, so the layout doesn't keep drifting once the wheel moves into a component
export function enableNestedSmoothScroll(onNestedScroll: () => void) {
  stopPageGlide = onNestedScroll;
  nativeTrackpad = isWebKit();
  window.addEventListener("wheel", onWheel, { passive: false, capture: true });
  return () => {
    stopPageGlide = () => {};
    window.removeEventListener("wheel", onWheel, { capture: true });
  };
}
